"use client"

/* eslint-disable no-shadow */
/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

import clsx from "clsx"
import { motion } from "framer-motion"
import type { Variants } from "framer-motion"
// import UniversalLink from '../../UniversalLink'
import Link from "next/link"
import { usePathname } from "next/navigation"
import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  type JSX,
} from "react"

import Arrow from "../../../public/icons/arrow-up.svg"
import JobCountPill from "../components/careers/JobCountPill"
import type { JobCountPillProps } from "../components/careers/JobCountPill"
import PageThemeContext from "../components/page-theme/PageThemeContext"
import PageGrid from "../components/PageGrid"

// NOTE: location property will be the page name including leading and trailing slashes
// for example, page ripple.com/careers -> location property is '/careers/'
interface MenuProps extends Omit<any, "children">, JobCountPillProps {
  parentLink: string
  menuLinks: Array<{
    text: string
    location: string
    onClick?: (e: any) => void // Optional onClick prop
  }>
  breakPoint?: "xl" | "lg" | "md" | "xs" | "none"
  ctaButton?: JSX.Element | null
  scroll?: boolean
  mode?: "light" | "dark"
}

const NavLink = ({ href, children, className, activeClassName, ...props }) => {
  const pathname = usePathname()

  const isActive = pathname === href

  return (
    <Link
      className={clsx(className, { [activeClassName]: isActive })}
      href={href}
      {...props}
    >
      {children}
    </Link>
  )
}

const SubNav: React.FC<MenuProps> = ({
  parentLink,
  menuLinks,
  jobCount,
  breakPoint = "lg",
  // ctaButton prop is only used on swell global, agenda and speaker pages
  ctaButton = null,
  scroll = false,
  mode,
}) => {
  const pathname = usePathname()
  const pageThemeContext = useContext(PageThemeContext)
  const themeColorClassOne =
    pageThemeContext.pageTheme.dark && mode !== "light"
      ? "text-gray-2.5"
      : "text-gray-90"

  const [showLinks, toggleShowLinks] = useState(false)

  const dynamicJobsRoute = "/careers/all-jobs/"
  const currentPage = useMemo(
    () =>
      menuLinks.find(
        ({ location: menuLinkLocation }) =>
          pathname === menuLinkLocation ||
          (pathname.startsWith(dynamicJobsRoute) &&
            dynamicJobsRoute === menuLinkLocation),
      ),
    [menuLinks, pathname],
  )

  const showJobCt = !!(
    currentPage?.location === "/careers/" ||
    "/careers/all-jobs/" ||
    "/careers/overview/" ||
    "/careers/early-careers" ||
    "/careers/engineering" ||
    "/careers/ripplelife"
  )

  // If page is resized, close the mobile subnav
  useEffect(() => {
    function handleResize() {
      toggleShowLinks(false)
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  })

  const openMenuVariants: Variants = {
    block: {
      opacity: 1,
      display: "block",
      transition: {
        duration: 0.2,
        when: "beforeChildren",
      },
    },
    hidden: {
      opacity: 0,
      transition: {
        duration: 0.2,
        when: "afterChildren",
      },
      transitionEnd: {
        display: "none",
      },
    },
  }

  const slideMenuVariants: Variants = {
    block: {
      transition: {
        duration: 0.2,
        staggerChildren: 0.1,
      },
    },
    hidden: {
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
      },
    },
  }

  const navItemVariants: Variants = {
    block: {
      opacity: 1,
    },
    hidden: {
      opacity: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
      },
    },
  }

  return (
    <div
      className={clsx(
        "relative w-full z-5",
        mode === "light"
          ? "bg-gray-0"
          : pageThemeContext.pageTheme.bgColorClass,
      )}
    >
      <PageGrid className="relative">
        <div
          className={clsx(
            "flex flex-wrap items-baseline my-4 col-span-full",
            !ctaButton && "justify-between",
          )}
        >
          <h4
            className={clsx(
              "p-4 heading2",
              ctaButton && "flex-1",
              themeColorClassOne,
            )}
          >
            {parentLink}
          </h4>
          <ul
            className={clsx(
              "hidden max-w-full p-4",
              ctaButton && "mr-8",
              breakPoint === "xl" && "xl:flex",
              breakPoint === "lg" && "lg:flex",
              breakPoint === "md" && "md:flex",
            )}
          >
            {menuLinks.map(({ location, text, onClick = () => {} }) => (
              <li
                onClick={onClick}
                key={location.replaceAll("/", "-")}
                className={clsx(
                  showJobCt &&
                    text.toLowerCase() === "view all jobs" &&
                    "relative",
                )}
              >
                <NavLink
                  className={clsx(
                    "ml-12",
                    pageThemeContext.pageTheme.dark && mode !== "light"
                      ? text === currentPage?.text
                        ? "text-gray-2.5 heading3"
                        : "text-gray-30 body3"
                      : text === currentPage?.text
                        ? "text-gray-90 heading3"
                        : "text-gray-60 body3",
                  )}
                  activeClassName={clsx(
                    "!text-bold !heading3",
                    pageThemeContext.pageTheme.dark && mode !== "light"
                      ? "!text-gray-2.5"
                      : "!text-gray-90",
                  )}
                  href={location}
                  scroll={scroll}
                >
                  {text}
                </NavLink>
                {showJobCt && text.toLowerCase() === "view all jobs" && (
                  <JobCountPill jobCount={jobCount} />
                )}
              </li>
            ))}
          </ul>
          {/* ctaButton prop is only used on swell global, agenda and speaker pages */}
          {ctaButton && <div className="hidden lg:block">{ctaButton}</div>}
          <div
            className={clsx(
              "p-4",
              breakPoint === "xl" && "xl:hidden",
              breakPoint === "lg" && "lg:hidden",
              breakPoint === "md" && "md:hidden",
            )}
          >
            <h2
              className={clsx(
                "flex items-center cursor-pointer",
                themeColorClassOne,
              )}
              onClick={() => toggleShowLinks(!showLinks)}
              aria-hidden="true"
            >
              {currentPage?.text}
              <Arrow
                alt="Arrow icon"
                className={clsx(
                  "ml-4 transform duration-300",
                  !showLinks && "rotate-180",
                  themeColorClassOne,
                )}
              />
            </h2>
          </div>
        </div>
      </PageGrid>
      <motion.div
        variants={openMenuVariants}
        animate={showLinks ? "block" : "hidden"}
        initial="hidden"
        className={clsx(
          "absolute w-full z-40",
          mode === "light"
            ? "bg-gray-0"
            : pageThemeContext.pageTheme.bgColorClass,
        )}
      >
        <div className="top-0 left-0 shadow-md">
          <PageGrid>
            <motion.ul
              variants={slideMenuVariants}
              className="p-4 col-span-full"
            >
              {menuLinks.map(({ location, text }) => (
                <motion.li
                  variants={navItemVariants}
                  key={location.replaceAll("/", "--")}
                  className={clsx(
                    "text-right",
                    showJobCt &&
                      text.toLowerCase() === "view all jobs" &&
                      "relative",
                  )}
                >
                  <NavLink
                    className={clsx(
                      "block pb-6 text-base",
                      pageThemeContext.pageTheme.dark && mode !== "light"
                        ? "text-gray-30"
                        : "text-gray-60",
                      {
                        "!hidden":
                          text.toLowerCase() === "view all jobs" &&
                          currentPage?.location === dynamicJobsRoute,
                      },
                    )}
                    href={location}
                    activeClassName="!hidden"
                    scroll={scroll}
                  >
                    {text}
                  </NavLink>
                  {showJobCt &&
                    text.toLowerCase() === "view all jobs" &&
                    currentPage?.location !== dynamicJobsRoute && (
                      <JobCountPill jobCount={jobCount} />
                    )}
                </motion.li>
              ))}
              {/* ctaButton prop is only used on swell global, agenda and speaker pages */}
              {ctaButton && (
                <motion.li
                  variants={navItemVariants}
                  key="ctaButtonKey"
                  className="float-right text-right"
                >
                  {ctaButton}
                </motion.li>
              )}
            </motion.ul>
          </PageGrid>
        </div>
      </motion.div>
    </div>
  )
}

export default SubNav
